// LO QUE LA PERSONA LEE CUANDO EL CONTRATO v2 CONTESTA UN ERROR.
//
// El servidor contesta `{"detail": {codigo, mensaje, que_hacer, pantalla},
// "detalle": mensaje}` (contrato v2 §1.1; `terminal/v2/errores.py`).
// `api.js` convierte eso en un `ErrorApi` cuyo `message` es `detalle` y,
// desde la auditoría F1 H-4, con `codigo` y `queHacer` del `detail`. Aquí
// se recompone lo que la pantalla tiene que decir —qué pasó y qué hacer—:
//
//   1. el código: el que traiga el error (`e.codigo`) MANDA; el que
//      corresponde al `status` en ESA ruta (`porEstado`) es solo el
//      respaldo para un servidor que no lo mande (un 409 puede ser
//      YA_VINCULADA o TOPE_DEL_PLAN: el status solo no lo distingue);
//   2. con un código conocido mandan los textos de DISEÑO-F1 §7, que son
//      los que la persona debe leer en la app;
//   3. sin código conocido, el mensaje del servidor y una indicación por
//      `status`; y si no se llegó al servidor, se dice eso.
import { motivoDeError } from '../ui/estados';

const TEXTOS = {
  CODIGO_DESCONOCIDO: {
    mensaje: 'Ese código no corresponde a ninguna terminal esperando.',
    queHacer: 'Revisa los 6 caracteres; si pasaron más de 10 minutos, la terminal ya enseña otro.',
  },
  NONCE_INVALIDO: {
    mensaje: 'El QR de la terminal ya cambió (cambia cada minuto).',
    queHacer: 'Vuelve a escanear la pantalla de la terminal.',
  },
  SIN_PERMISO: {
    mensaje: ({ terminal }) => `No tienes permiso para operar ${terminal || 'esa terminal'}.`,
    queHacer: 'Pídeselo a tu encargado.',
  },
  PIN_DEBE_CAMBIAR: {
    mensaje: 'Por seguridad, cambia tu PIN en Mi cuenta: una terminal que usaste fue reportada como robada.',
    queHacer: 'Mientras tanto entras con el QR.',
  },
  YA_VINCULADA: {
    mensaje: 'Esa terminal ya está vinculada.',
    queHacer: 'Si es tuya y quieres volver a vincularla, en su tarjeta pulsa «Desvincular y volver a vincular».',
  },
  CODIGO_VENCIDO: {
    mensaje: 'Ese código ya venció.',
    queHacer: 'La terminal enseña otro: usa ése.',
  },
  TOPE_DEL_PLAN: {
    mensaje: 'Tu plan ya tiene todas las terminales que admite.',
    queHacer: 'Desvincula una que no uses o amplía el plan en Suscripción.',
  },
  DEMASIADAS_PETICIONES: {
    mensaje: 'Se hicieron demasiados intentos seguidos desde esta conexión.',
    queHacer: 'Espera unos minutos y vuelve a intentarlo.',
  },
};

const POR_STATUS = {
  401: 'Tu sesión terminó. Vuelve a entrar y repite la acción.',
  403: 'Pídele a tu encargado el permiso para esto.',
  404: 'Revisa el código y vuelve a intentarlo.',
  409: 'Recarga la lista de terminales para ver su estado actual.',
  429: 'Espera unos minutos y vuelve a intentarlo.',
};

const SIN_SERVIDOR = {
  mensaje: 'No hubo conexión con el servidor.',
  queHacer: 'Revisa tu señal y vuelve a intentarlo.',
};

/**
 * `{ codigo, mensaje, queHacer }` para pintar. `porEstado` traduce el
 * `status` al código del contrato en la ruta que falló, p. ej.
 * `{ 404: 'NONCE_INVALIDO', 403: 'SIN_PERMISO' }`.
 */
export function leerErrorDelContrato(e, { porEstado = {}, terminal } = {}) {
  const status = e && typeof e === 'object' ? e.status : null;
  const codigo = (e && e.codigo) || (status && porEstado[status]) || null;
  const conocido = codigo ? TEXTOS[codigo] : null;
  if (conocido) {
    const mensaje = typeof conocido.mensaje === 'function' ? conocido.mensaje({ terminal }) : conocido.mensaje;
    // Cuando el servidor nombra la terminal, su frase es más precisa que
    // la plantilla: se prefiere si trae el nombre.
    const delServidor = codigo === 'SIN_PERMISO' && status && !terminal ? motivoDeError(e) : null;
    return { codigo, mensaje: delServidor || mensaje, queHacer: conocido.queHacer };
  }
  if (!status) {
    const texto = motivoDeError(e);
    return { codigo: null, mensaje: texto || SIN_SERVIDOR.mensaje, queHacer: SIN_SERVIDOR.queHacer };
  }
  return {
    codigo,
    mensaje: motivoDeError(e) || 'No se pudo completar lo que pediste.',
    queHacer: (e && e.queHacer) || POR_STATUS[status] || (status >= 500
      ? 'El servidor no contestó: espera unos segundos y vuelve a intentarlo.'
      : 'Vuelve a intentarlo.'),
  };
}
